/**
 * Platform Admin API
 *
 * API client for Platform Admin functionality:
 * - Tenant CRUD
 * - Platform statistics
 * - Plan information
 *
 * All endpoints require Platform Admin role.
 */

import { request, type RequestOptions } from './core'
import type {
  Tenant,
  TenantDetail,
  TenantList,
  TenantCreate,
  TenantUpdate,
  TenantSearchParams,
  PlatformStats,
  PlanDefinition,
  PlanComparison,
} from '../../types/tenant'

const BASE_PATH = '/v1/platform-admin'

// =============================================================================
// PLATFORM STATS
// =============================================================================

/**
 * Get platform-wide statistics.
 * Returns counts for tenants, users, projects, agents.
 */
export async function getPlatformStats(): Promise<PlatformStats> {
  return request<PlatformStats>(`${BASE_PATH}/tenants/stats`)
}

// =============================================================================
// TENANT CRUD
// =============================================================================

/**
 * List all tenants with filtering and pagination.
 */
export async function listTenants(
  params: TenantSearchParams = {},
  options?: Pick<RequestOptions, 'tenantId'>,
): Promise<TenantList> {
  const queryParams = new URLSearchParams()

  if (params.search) queryParams.set('search', params.search)
  if (params.plan) queryParams.set('plan', params.plan)
  if (params.status) queryParams.set('status', params.status)
  if (params.page) queryParams.set('page', String(params.page))
  if (params.page_size) queryParams.set('page_size', String(params.page_size))
  if (params.order_by) queryParams.set('order_by', params.order_by)
  if (params.order_desc !== undefined) queryParams.set('order_desc', String(params.order_desc))

  const query = queryParams.toString()
  const querySuffix = query ? `?${query}` : ''
  return request<TenantList>(`${BASE_PATH}/tenants${querySuffix}`, options)
}

/**
 * Get a single tenant by ID with full details.
 */
export async function getTenant(tenantId: string): Promise<TenantDetail> {
  return request<TenantDetail>(`${BASE_PATH}/tenants/${tenantId}`)
}

/**
 * Create a new tenant.
 */
export async function createTenant(data: TenantCreate): Promise<Tenant> {
  return request<Tenant>(`${BASE_PATH}/tenants`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update an existing tenant.
 */
export async function updateTenant(tenantId: string, data: TenantUpdate): Promise<Tenant> {
  return request<Tenant>(`${BASE_PATH}/tenants/${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete a tenant.
 * @param hardDelete If true, permanently deletes. If false, marks as cancelled.
 */
export async function deleteTenant(tenantId: string, hardDelete: boolean = false): Promise<void> {
  return request<void>(`${BASE_PATH}/tenants/${tenantId}?hard_delete=${hardDelete}`, {
    method: 'DELETE',
  })
}

// =============================================================================
// QUOTA MANAGEMENT
// =============================================================================

/**
 * Get quota usage for a tenant.
 */
export async function getTenantQuotaUsage(
  tenantId: string
): Promise<{ tenant_id: string; quota_usage: Record<string, { used: number; limit: number }> }> {
  return request(`${BASE_PATH}/tenants/${tenantId}/quota-usage`)
}

// =============================================================================
// PLANS (Read-only)
// =============================================================================

/**
 * List all available subscription plans.
 */
export async function listPlans(): Promise<PlanDefinition[]> {
  return request<PlanDefinition[]>(`${BASE_PATH}/plans`)
}

/**
 * Get details for a specific plan.
 */
export async function getPlanDetails(
  planTier: string
): Promise<PlanDefinition & { quotas_formatted: Record<string, string> }> {
  return request(`${BASE_PATH}/plans/${planTier}`)
}

/**
 * Get plan comparison data for UI display.
 */
export async function comparePlans(): Promise<PlanComparison> {
  return request<PlanComparison>(`${BASE_PATH}/plans/compare/all`)
}

// =============================================================================
// TENANT FEATURE FLAGS
// =============================================================================

import type { FeatureFlagCategory } from '../../constants/featureFlags'

export type FeatureStage = 'alpha' | 'beta' | 'rc' | 'ga'

/**
 * User-facing labels for lifecycle stages. The stage *values* above are the
 * API contract; these are display-only. Keep every surface (dropdown, tab
 * chips, toasts) reading from here so naming stays consistent.
 */
export const STAGE_LABELS: Record<FeatureStage, string> = {
  alpha: 'Alpha',
  beta: 'Beta',
  rc: 'RC', // Release Candidate
  ga: 'GA', // General Availability
}

/** Full, spelled-out stage names — used for tooltips / a11y where the short label is cryptic. */
export const STAGE_FULL_LABELS: Record<FeatureStage, string> = {
  alpha: 'Alpha',
  beta: 'Beta',
  rc: 'Release Candidate',
  ga: 'General Availability',
}

export interface TenantFlagRead {
  flag_key: string
  enabled: boolean | null // null = inherits from customer
  inherited_value: boolean
  is_overridden: boolean
  stage: FeatureStage
  category: FeatureFlagCategory
  display_name: string | null
}

export interface TenantFlagsResponse {
  tenant_id: string
  tenant_name: string
  flags: TenantFlagRead[]
  override_count: number
}

export interface TenantFlagUpdate {
  flag_key: string
  enabled: boolean | null // null to inherit from customer
}

export interface CustomerFlagRead {
  flag_key: string
  enabled: boolean
  stage: FeatureStage
  category: FeatureFlagCategory
  display_name: string | null
  description: string | null
}

export interface CustomerFlagListResponse {
  flags: CustomerFlagRead[]
  total: number
  categories: string[]
}

export interface CustomerFlagBulkUpdateResponse {
  updated_count: number
  flags: CustomerFlagRead[]
}

export interface FeatureFlagTemplateRead {
  id: string
  slug: string
  name: string
  description: string | null
  is_system: boolean
  enabled_flag_keys: string[]
  enabled_prefixes: string[]
  sort_order: number
  created_at: number | null
  updated_at: number | null
}

export interface FeatureFlagTemplateLastApplied {
  template_id: string | null
  template_name: string | null
  applied_at: number
  can_undo: boolean
}

export interface FeatureFlagTemplateListResponse {
  templates: FeatureFlagTemplateRead[]
  last_applied: FeatureFlagTemplateLastApplied | null
}

export interface FeatureFlagTemplatePreview {
  template_id: string
  template_name: string
  scope: string
  will_enable: string[]
  will_disable: string[]
  unchanged: string[]
}

export interface FeatureFlagTemplateApplyResponse extends FeatureFlagTemplatePreview {
  updated_count: number
}

export type FeatureFlagTemplateScope = 'customer' | 'tenant'

export async function listFeatureFlagTemplates(
  scope: FeatureFlagTemplateScope = 'customer',
  tenantId?: string,
): Promise<FeatureFlagTemplateListResponse> {
  const params = new URLSearchParams()
  params.set('scope', scope)
  if (tenantId) params.set('tenant_id', tenantId)
  return request<FeatureFlagTemplateListResponse>(
    `${BASE_PATH}/feature-flag-templates?${params.toString()}`,
  )
}

export async function createFeatureFlagTemplate(data: {
  name: string
  description?: string
  enabled_flag_keys?: string[]
  clone_from_id?: string
}): Promise<FeatureFlagTemplateRead> {
  return request<FeatureFlagTemplateRead>(`${BASE_PATH}/feature-flag-templates`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateFeatureFlagTemplate(
  templateId: string,
  data: { name?: string; description?: string; enabled_flag_keys?: string[] },
): Promise<FeatureFlagTemplateRead> {
  return request<FeatureFlagTemplateRead>(`${BASE_PATH}/feature-flag-templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteFeatureFlagTemplate(templateId: string): Promise<void> {
  await request<void>(`${BASE_PATH}/feature-flag-templates/${templateId}`, { method: 'DELETE' })
}

export async function previewFeatureFlagTemplate(
  templateId: string,
  scope: FeatureFlagTemplateScope,
  tenantId?: string,
): Promise<FeatureFlagTemplatePreview> {
  const params = new URLSearchParams()
  params.set('scope', scope)
  if (tenantId) params.set('tenant_id', tenantId)
  return request<FeatureFlagTemplatePreview>(
    `${BASE_PATH}/feature-flag-templates/${templateId}/preview?${params.toString()}`,
  )
}

export async function applyFeatureFlagTemplate(
  templateId: string,
  scope: FeatureFlagTemplateScope,
  tenantId?: string,
): Promise<FeatureFlagTemplateApplyResponse> {
  return request<FeatureFlagTemplateApplyResponse>(
    `${BASE_PATH}/feature-flag-templates/${templateId}/apply`,
    {
      method: 'POST',
      body: JSON.stringify({ scope, tenant_id: tenantId ?? null }),
    },
  )
}

export async function undoFeatureFlagTemplateApply(
  scope: FeatureFlagTemplateScope,
  tenantId?: string,
): Promise<FeatureFlagTemplateApplyResponse> {
  return request<FeatureFlagTemplateApplyResponse>(`${BASE_PATH}/feature-flag-templates/undo`, {
    method: 'POST',
    body: JSON.stringify({ scope, tenant_id: tenantId ?? null }),
  })
}

/**
 * Get feature flags for a specific tenant.
 * Shows overrides and inherited values.
 */
export async function getTenantFlags(tenantId: string): Promise<TenantFlagsResponse> {
  return request<TenantFlagsResponse>(`${BASE_PATH}/features/tenants/${tenantId}`)
}

/**
 * List customer-level feature flags.
 */
export async function listCustomerFlags(): Promise<CustomerFlagListResponse> {
  return request<CustomerFlagListResponse>(`${BASE_PATH}/features`)
}

/**
 * Update a single customer-level feature flag.
 */
export async function updateCustomerFlag(
  flagKey: string, 
  updates: { enabled?: boolean; stage?: FeatureStage }
): Promise<CustomerFlagRead> {
  const params = new URLSearchParams()
  if (updates.enabled !== undefined) params.append('enabled', String(updates.enabled))
  if (updates.stage !== undefined) params.append('stage', updates.stage)
  
  return request<CustomerFlagRead>(`${BASE_PATH}/features/${flagKey}?${params.toString()}`, {
    method: 'PUT',
  })
}

/**
 * Bulk update customer-level feature flags.
 */
export async function bulkUpdateCustomerFlags(
  flags: { flag_key: string; enabled?: boolean; stage?: FeatureStage }[]
): Promise<CustomerFlagBulkUpdateResponse> {
  return request<CustomerFlagBulkUpdateResponse>(`${BASE_PATH}/features`, {
    method: 'PUT',
    body: JSON.stringify({ flags }),
  })
}

/**
 * Update a single tenant flag override.
 * Set enabled=null to inherit from customer level.
 */
export async function updateTenantFlag(
  tenantId: string,
  flagKey: string,
  enabled: boolean | null
): Promise<TenantFlagRead> {
  const query = enabled === null ? '' : `?enabled=${enabled}`
  return request<TenantFlagRead>(`${BASE_PATH}/features/tenants/${tenantId}/${flagKey}${query}`, {
    method: 'PUT',
  })
}

/**
 * Bulk update tenant flag overrides.
 */
export async function bulkUpdateTenantFlags(
  tenantId: string,
  flags: TenantFlagUpdate[]
): Promise<TenantFlagsResponse> {
  return request<TenantFlagsResponse>(`${BASE_PATH}/features/tenants/${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify({ flags }),
  })
}

/**
 * Reset all tenant flag overrides to inherit from customer.
 */
export async function resetTenantFlags(tenantId: string): Promise<void> {
  return request<void>(`${BASE_PATH}/features/tenants/${tenantId}/overrides`, {
    method: 'DELETE',
  })
}

// =============================================================================
// CROSS-TENANT COMPARISON
// =============================================================================

import type {
  TenantPlanComparisonResponse,
  TenantFinOpsResponse,
  TenantUsageResponse,
  TenantUserMetricsResponse,
  UserActivityResponse,
} from '../../types/comparison'
import { appendMetricsDateRangeToSearchParams, type MetricsDateRange } from '../../types/metricsDateRange'

function comparisonQuery(
  endpoint: string,
  dateRange?: MetricsDateRange,
  extra?: Record<string, string>
): string {
  const params = new URLSearchParams()
  if (dateRange) appendMetricsDateRangeToSearchParams(params, dateRange)
  if (extra) Object.entries(extra).forEach(([k, v]) => params.set(k, v))
  const qs = params.toString()
  const querySuffix = qs ? `?${qs}` : ''
  return `${BASE_PATH}/comparison/${endpoint}${querySuffix}`
}

export async function getComparisonPlans(params?: { search?: string }): Promise<TenantPlanComparisonResponse> {
  const extra: Record<string, string> = {}
  if (params?.search) extra.search = params.search
  const qs = new URLSearchParams(extra).toString()
  const querySuffix = qs ? `?${qs}` : ''
  return request<TenantPlanComparisonResponse>(`${BASE_PATH}/comparison/plans${querySuffix}`)
}

export async function getComparisonFinOps(
  dateRange?: MetricsDateRange,
  params?: { search?: string }
): Promise<TenantFinOpsResponse> {
  const extra: Record<string, string> = {}
  if (params?.search) extra.search = params.search
  return request<TenantFinOpsResponse>(comparisonQuery('finops', dateRange, extra))
}

export async function getComparisonUsage(
  dateRange?: MetricsDateRange,
  params?: { search?: string }
): Promise<TenantUsageResponse> {
  const extra: Record<string, string> = {}
  if (params?.search) extra.search = params.search
  return request<TenantUsageResponse>(comparisonQuery('usage', dateRange, extra))
}

export async function getComparisonUsers(
  dateRange?: MetricsDateRange,
  params?: { search?: string }
): Promise<TenantUserMetricsResponse> {
  const extra: Record<string, string> = {}
  if (params?.search) extra.search = params.search
  return request<TenantUserMetricsResponse>(comparisonQuery('users', dateRange, extra))
}

export async function getComparisonUserActivity(
  dateRange?: MetricsDateRange,
  params?: { page?: number; page_size?: number; search?: string; tenant_id?: string; order_by?: string; order_desc?: boolean }
): Promise<UserActivityResponse> {
  const extra: Record<string, string> = {}
  if (params?.page) extra.page = String(params.page)
  if (params?.page_size) extra.page_size = String(params.page_size)
  if (params?.search) extra.search = params.search
  if (params?.tenant_id) extra.tenant_id = params.tenant_id
  if (params?.order_by) extra.order_by = params.order_by
  if (params?.order_desc !== undefined) extra.order_desc = String(params.order_desc)
  return request<UserActivityResponse>(comparisonQuery('user-activity', dateRange, extra))
}

// =============================================================================
// EXPORT ALL
// =============================================================================

export const platformAdminApi = {
  // Stats
  getPlatformStats,

  // Tenants
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  deleteTenant,

  // Quotas
  getTenantQuotaUsage,

  // Plans
  listPlans,
  getPlanDetails,
  comparePlans,

  // Tenant Feature Flags
  listCustomerFlags,
  updateCustomerFlag,
  bulkUpdateCustomerFlags,
  getTenantFlags,
  updateTenantFlag,
  bulkUpdateTenantFlags,
  resetTenantFlags,

  listFeatureFlagTemplates,
  createFeatureFlagTemplate,
  updateFeatureFlagTemplate,
  deleteFeatureFlagTemplate,
  previewFeatureFlagTemplate,
  applyFeatureFlagTemplate,
  undoFeatureFlagTemplateApply,

  // Cross-Tenant Comparison
  getComparisonPlans,
  getComparisonFinOps,
  getComparisonUsage,
  getComparisonUsers,
  getComparisonUserActivity,
}
